import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API, getStoredSession, getAuthHeaders, clearSession } from '@/utils/api';

export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was passed from AuthCallback
  const passedUser = location.state?.user;

  // Check localStorage for existing session
  const { user: storedUser, token: storedToken } = getStoredSession();

  // Initialize state based on passed user or stored user
  const [isAuthenticated, setIsAuthenticated] = useState(passedUser || storedUser ? true : null);
  const [user, setUser] = useState(passedUser || storedUser || null);
  const hasCheckedRef = useRef(!!(passedUser || storedUser));

  const checkAuth = useCallback(async () => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      clearSession();
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (storedUser && !passedUser) {
      // Verify stored session is still valid
      axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      }).then(response => {
        setUser(response.data);
        setIsAuthenticated(true);
      }).catch(() => {
        clearSession();
        setIsAuthenticated(false);
      });
    } else if (!passedUser && !storedUser) {
      checkAuth();
    }
  }, [checkAuth, passedUser, storedUser]);

  // Show loading while checking
  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Always redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Render children with user context
  return React.cloneElement(children, { user });
}
